/**
 * Contrat de types des dialogues. Recopie exacte de la section "Types (contrat
 * exact)" de docs/design/07-DIALOGUE-FORMAT.md : ne pas devier sans mettre a
 * jour le document en meme temps (voir AGENTS.md, regle 7 sur les ADR pour
 * tout changement structurant).
 */

import type { Attribute, DifficultyName, Skill } from '@/rules/attributes';
import type { CharacterId } from '@/rules/character';
import { CHARACTER_IDS, getCharacter } from '@/rules/character';
// Import de TYPE seul (efface a la compilation) : voir le commentaire de
// `SfxId` dans src/audio/sfx.ts -- ne rend jamais src/narrative dependant du
// DOM/Web Audio (regle d'AGENTS.md n°2, testabilite Node, ADR 0023).
import type { SfxId } from '@/audio/sfx';

export type SpeakerId =
  | CharacterId
  | 'narrateur'
  | 'directeur'
  | 'instructeur'
  | 'otage'
  | 'radio'
  // Chapitre 2 (ADR 0023) : six locuteurs non-cadets nouveaux. Faute de
  // portrait livre, src/ui/portraits.ts affiche l'initiale sur une couleur
  // (Smith a deja son image, voir PORTRAIT_SOURCES).
  | 'smith'
  | 'enfant'
  | 'murano'
  | 'guide'
  | 'charcudoc'
  | 'ganger'
  // Scene 12 (lot 5.14) : l'inconnue du Blue Purple, portrait P19.
  | 'inconnue';

/**
 * Alias de locuteur/jet resolus a l'execution depuis `RunState.roster` (ADR
 * 0014 §7, lot 3.1) : `equipier1`/`equipier2` (coequipiers de Franklyn, dans
 * l'ordre du tirage) et `rivale` (capitaine adverse, Abigail au chapitre 1).
 * Voir src/narrative/aliases.ts pour la resolution. Jamais dans les donnees
 * PRESENTEES (`PresentedNode.lines[].who`, `PresentedRoll.who`) : toujours un
 * `CharacterId` deja resolu a ce stade, pour que les portraits fonctionnent.
 */
export type TeamAlias = 'equipier1' | 'equipier2' | 'rivale';

export interface DialogueFile {
  /** Ex. "ch1.hub.john". */
  id: string;
  /** Interlocuteur principal, pour le portrait. */
  speaker?: SpeakerId;
  /** Cle d'un noeud. */
  start: string;
  /**
   * Noeuds accessibles UNIQUEMENT comme point d'entree alternatif (ex.
   * `startNode` d'une entite d'exploration, lot 3.5+) : le validateur ne les
   * signale pas "inatteignable depuis start" tant qu'ils le sont depuis l'une
   * de ces entrees (voir validate.ts).
   */
  entries?: string[];
  nodes: Record<string, DialogueNode>;
  /**
   * Decor plein cadre par defaut de tout le fichier (ADR 0023) : une cle de
   * `src/data/backdrops.ts`. Un `DialogueNode.backdrop` sur le noeud courant
   * l'emporte (voir sa doc) ; sans aucun des deux, l'ancienne table de
   * src/ui/sceneChrome.ts reste le repli (chapitre 1, inchange).
   */
  backdrop?: string;
}

export interface DialogueNode {
  /** Narration, a la troisieme personne. */
  text?: string;
  lines?: DialogueLine[];
  /** Appliques a l'entree du noeud, une seule fois. */
  effects?: Effect[];
  /**
   * Jet de "reflexion" prealable aux choix de ce noeud (examen ecrit,
   * ADR 0012) : tant qu'il n'est pas resolu, le noeud est en attente et
   * `choose()` refuse tout choix. Sa reussite revele le choix `best` du
   * noeud (`PresentedChoice.best`), son echec ne revele rien.
   */
  insight?: InsightSpec;
  /**
   * RAPPEL : identifiant d'un autre noeud dont les repliques sont reaffichees en tete de
   * celui-ci, estompees, avant son propre contenu. Repond a un defaut constate en jeu pendant
   * l'examen ecrit : la question du surveillant est posee sur un noeud, et la triche
   * (regarder la copie voisine, glisser une reponse) emmene le joueur sur un AUTRE noeud, ou
   * il ne reste plus que "Reste a repondre" et trois reponses sans enonce. "Prevoir que la
   * question reste visible apres les interactions/jets de des."
   *
   * On POINTE le noeud source plutot que de recopier son texte : une question reecrite ne peut
   * pas se desynchroniser de son rappel. Seules les `lines` sont reprises, jamais la narration
   * -- c'est l'enonce qu'on rappelle, pas la mise en place, qui elle appartient au moment ou
   * elle a ete jouee. Le validateur verifie que la cible existe et porte bien des repliques.
   */
  recall?: string;
  /** Si absent : enchainement automatique via `to`. */
  choices?: DialogueChoice[];
  /** Noeud suivant ; absent et sans choix = fin. */
  to?: string;
  /**
   * Remplace le decor a l'entree de CE noeud (ADR 0023) : une cle de
   * `src/data/backdrops.ts`, prioritaire sur `DialogueFile.backdrop`. Un
   * changement de decor entre deux noeuds se fait en coupe franche (jamais de
   * fondu) -- c'est ce qui permet une suite d'images (le slow, la rafale)
   * sans nouveau type de noeud.
   */
  backdrop?: string;
  /**
   * Bruitages synthetises joues a l'entree du noeud (ADR 0023), une seule
   * fois -- memes recettes que le combat tactique (`src/audio/sfx.ts`, ADR
   * 0010). `music` est reserve a la piste du slow (lot 5.12) : absent du
   * type tant qu'elle n'existe pas.
   */
  sound?: { sfx?: SfxId[] };
}

export interface DialogueLine {
  who: SpeakerId | TeamAlias;
  text: string;
  /**
   * Variante du portrait de `who` pour CETTE replique (ADR 0028), ex. `"blesse"` pour
   * Zachary mourant aux egouts. Facultative : absente, le portrait par defaut. Les variantes
   * sont declarees par locuteur dans `src/ui/portraits.ts` (`PORTRAIT_VARIANTS`) ; une
   * variante inconnue, ou posee sur un alias d'equipe, est une anomalie de `validateDialogue`.
   */
  portrait?: string;
}

export interface DialogueChoice {
  /** Prefixe [Competence] quand il y a un jet. */
  text: string;
  /** Sans `check`. */
  to?: string;
  /** Avec `check` : `onSuccess` ET `onFailure` obligatoires. */
  check?: CheckSpec;
  onSuccess?: string;
  onFailure?: string;
  /** Toutes doivent etre vraies pour que le choix existe. */
  conditions?: Condition[];
  /** Appliques dans tous les cas. */
  effects?: Effect[];
  successEffects?: Effect[];
  failureEffects?: Effect[];
  /**
   * Marque LA meilleure reponse du noeud (jugement institutionnel, pas la
   * doctrine du joueur -- voir ADR 0012). Un seul `best` par noeud, et
   * uniquement dans un noeud portant `insight` (verifie par le validateur).
   * N'est jamais expose au joueur (`PresentedChoice.best`) tant que le jet de
   * reflexion du noeud n'a pas reussi.
   */
  best?: true;
}

export interface CheckSpec {
  /** Cle de SKILLS. */
  skill: Skill;
  /** Par defaut SKILL_ATTRIBUTE[skill]. */
  attribute?: Attribute;
  /**
   * "FACILE" | "NORMALE" | ... — jamais un nombre. Sert de repli quand
   * `dvByCounter` est absent OU que son compteur n'est pas encore pose (voir
   * `dvByCounter` ci-dessous) : toujours obligatoire, meme avec `dvByCounter`.
   */
  dv: DifficultyName;
  /** Cadet qui lance le jet (un alias d'equipe resout vers un coequipier). Par defaut le candidat (Franklyn). */
  who?: CharacterId | TeamAlias;
  /**
   * DV VARIABLE, pilotee par un compteur de `RunState.flags` (ADR 0015 §3 --
   * la vigilance du surveillant a l'examen ecrit : chaque tentative de triche
   * la fait monter, la DV de Discretion suit). DV effective =
   * `levels[min(valeur du compteur, levels.length - 1)]`, jamais negatif
   * (compteur absent ou < 0 traite comme 0). `dv` reste le repli tant que le
   * compteur vaut 0 ou est absent -- les deux doivent donc s'accorder dans les
   * donnees. Remplace le besoin de dupliquer un noeud de jet par palier de
   * difficulte : un seul `check`/`insight` couvre tous les niveaux.
   */
  dvByCounter?: { counter: string; levels: DifficultyName[] };
}

/**
 * Jet de "reflexion" d'un noeud (ADR 0012) : un `CheckSpec` ordinaire, plus
 * la narration facultative jouee apres resolution et les effets facultatifs
 * qui en decoulent (etiquettes de personnalite, par ex. -- voir
 * ch1.exam.json). Resolu par `DialogueRunner.rollInsight()`, jamais par
 * `choose()`.
 */
export interface InsightSpec extends CheckSpec {
  /** Narration jouee si le jet reussit (facultatif). */
  successText?: string;
  /** Narration jouee si le jet echoue (facultatif). */
  failureText?: string;
  successEffects?: Effect[];
  failureEffects?: Effect[];
  /**
   * Reflexion FACULTATIVE (ADR 0015 §1, ex. l'examen ecrit revu) : le noeud
   * n'est PAS bloque, `choose()` fonctionne directement tant que le jet n'est
   * pas tire (`PresentedInsight.status === 'available'`). Absent : comportement
   * mandatory inchange (ADR 0012), `choose()` refuse tant que `rollInsight()`
   * n'a pas ete appele.
   */
  optional?: true;
  /**
   * Cout en compteur de `RunState.flags` (ex. concentration de l'examen),
   * consomme par `rollInsight()` -- qui refuse alors avec
   * `{ ok: false, reason: "Plus de concentration." }` si insuffisant. N'a de
   * sens qu'avec `optional: true` (verifie par le validateur).
   */
  cost?: { counter: string; amount: number };
}

export type Condition =
  | { flag: string; equals?: string | number | boolean; atLeast?: number }
  | { tag: string }
  | { affinity: CharacterId; atLeast?: number; atMost?: number }
  /**
   * Vrai si `cadet` fait partie de l'equipe bleue de Franklyn (`RunState.roster.blue`),
   * peu importe l'ordre du tirage (lot 3.4, ADR 0014 §7). C'est le mecanisme
   * retenu pour les variantes de contenu par cadet ("le fourgon, les salles ...
   * doivent etre reecrits pour des coequipiers variables") : plus fiable qu'un
   * flag conventionnel (`ch1.equipe.john`, evoque a l'origine par l'ADR) car
   * calcule directement depuis le roster, jamais desynchronise. `franklyn`
   * comme `abigail` n'ont pas de sens ici (le premier est toujours dans
   * l'equipe bleue, la seconde jamais) -- toujours vrai pour l'un, toujours
   * faux pour l'autre, mais aucun garde-fou moteur ne l'empeche.
   */
  | { teammate: CharacterId }
  /**
   * Vrai selon `RunState.tempo`, le minuteur invisible avance par l'effet
   * `{ tempo: n }` (ADR 0023, chapitre 2 -- une consequence qui depend du
   * temps perdu pendant la fuite). Les deux bornes sont facultatives, comme
   * `affinity` ; sans aucune des deux, la condition est toujours vraie.
   */
  | { tempo: { atLeast?: number; atMost?: number } }
  | { not: Condition }
  | { all: Condition[] }
  | { any: Condition[] };

export type Effect =
  | { affinity: { who: CharacterId | TeamAlias; delta: number } } // borne a [-3, +3]
  | { tag: string } // etiquette de dossier
  | { entry: { key: string; label: string; value: string } }
  | { flag: string; value: string | number | boolean } // drapeau de partie (volatil)
  /**
   * Drapeau numerique incremente de `delta` ; `min`/`max` (ADR 0023,
   * facultatifs) bornent la valeur APRES le delta -- l'etat de blessure de
   * Letitia (0 a 3, chapitre 2) en est le premier usage. Sans les deux, le
   * compteur reste illimite comme avant l'ADR.
   */
  | { counter: string; delta: number; min?: number; max?: number }
  | { tempo: number } // avance le minuteur invisible
  | { team: TeamEffect } // materiel de l'equipe du joueur
  | { writtenScore: { counterKey: string; total: number } }; // finalise la note ecrite (ADR 0012)

export type TeamEffect =
  | { healkits: number } // delta
  | { extraTaser: true }
  | { gassed: CharacterId | TeamAlias };

/**
 * Libelles francais affichables pour chaque locuteur. Les cadets prennent
 * leur prenom depuis la fiche (`getCharacter`) : une seule source de verite
 * pour l'orthographe des noms.
 */
const STATIC_SPEAKER_LABELS: Record<Exclude<SpeakerId, CharacterId>, string> = {
  narrateur: '',
  directeur: 'Le directeur',
  instructeur: "L'instructeur",
  otage: "L'otage",
  radio: 'Radio',
  // Chapitre 2 (ADR 0023).
  smith: 'Smith',
  enfant: "L'enfant",
  murano: 'Murano',
  guide: 'Le guide',
  charcudoc: 'Le charcudoc',
  ganger: 'Le ganger',
  inconnue: "L'inconnue",
};

function buildSpeakerLabels(): Record<SpeakerId, string> {
  const labels = { ...STATIC_SPEAKER_LABELS } as Record<SpeakerId, string>;
  for (const id of CHARACTER_IDS) {
    labels[id] = getCharacter(id).name;
  }
  return labels;
}

export const SPEAKER_LABELS: Record<SpeakerId, string> = buildSpeakerLabels();
